import { useEffect, useState, useContext, useCallback } from "react";
import GridPageLayout from "core/components/layout/grid/gridLayout/gridPageLayout";
import { DataTableGrid } from "./incidents.styled";
import DarkDatagrid from "core/components/global/tables/datagrid/dataGrid";
import { IHeadCell } from "core/components/global/tables/datagrid/dataGridHeader/datagridHeader";
import IncidentsToolbar from "./incidentsToolbar";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import {
  assignUserToIncident,
  fetchAllIncidentsByPage,
  setExportLoading,
  setIncidentStatus,
  setIncidentsByPage,
  setSelectedRow,
  setSideModal,
  updatePage,
} from "modules/flywheel/store/incident";
import GridItem from "core/components/layout/grid/gridItem/gridItem";
import { exportToCSV } from "core/hooks/tableToCsv";
import { UserClaims, AuthContext } from "core/contexts/auth/auth";
import { getUsersService } from "core/services/ac2Flywheel/userService";
import { useTheme } from "styled-components";
import { getCasesMetrics } from "modules/flywheel/services/incidentService";
import { getFilterCount, getFilters } from "core/hooks/filters";
import { setFiltersApplied } from "core/store/shared/header";
import { useChatbot } from "./searchAgent/chatBotContext";
import { useNavigate } from "react-router";
import AlertSidePanel from "./summarySidebar/alertSidePanel";
export interface ISortData {
  field: string;
  order: string;
}

export interface ShowMoreState {
  [key: string]: boolean;
}

interface Params {
  target: string;
  aFAccessToken: string;
  page: number;
  size: number;
  orderBy: string;
  order: string;
  filters: object;
  search: string;
}

// aFAccessToken: string; size: string; page: string; order: string; orderBy: string; filers: [];

export default function IncidentTable() {
  const theme = useTheme();
  const { checkAuthToken } = useContext(AuthContext);
  const incidentState = useAppSelector((state) => state.incidents);
  const dispatch = useAppDispatch();
  const [users, setUsers] = useState<UserClaims[]>([]);
  const navigate = useNavigate();

  const { showChatbot } = useChatbot();
  const [topGridBoxdata, setTopGridBoxdata] = useState<any>([
    { num: 0, type: "Total Cases", color: "fff" },
    { num: 0, type: "Critical", color: "fff" },
    { num: 0, type: "High", color: "fff" },
    { num: 0, type: "Medium", color: "fff" },
    { num: 0, type: "Low", color: "fff" },
    { num: 0, type: "Informative", color: "fff" },
    { num: 0, type: "Unclassified", color: "fff" },
  ]);

  const statusMapping = {
    Open: theme.colors.open_incident,
    "In Progress": theme.colors.in_progress_incident,
    Closed: theme.colors.closed_incident,
    "Request Tuning": theme.colors.requested_incident,
  };

  /**
   * Fetches the full list of active and inactive users on this platform
   */
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = await checkAuthToken();
        const config = {
          Authorization: token,
          "Content-Type": "application/json",
        };
        const usersData = await getUsersService(config, "socai");
        setUsers(usersData);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchUsers();
  }, []);

  const handleSetStatus = async (status: string, id: string) => {
    if (status) {
      const token = await checkAuthToken();
      dispatch(
        setIncidentStatus({ aFAccessToken: token, status: status, id: id })
      );
    }
  };

  /**
   * Function to manage the assigned to field
   * @param email
   * @param id
   */
  const handleAssignedTo = async (email: string, id: string) => {
    const token = await checkAuthToken();
    if (email) {
      dispatch(
        assignUserToIncident({ aFAccessToken: token, email: email, id: id })
      );
    } else {
      dispatch(
        assignUserToIncident({ aFAccessToken: token, email: "", id: id })
      );
    }
  };

  async function getGridBoxData() {
    const token = await checkAuthToken();
    const config = { Authorization: token, "Content-Type": "application/json" };
    const gridBoxData = await getCasesMetrics(config);
    setTopGridBoxdata(gridBoxData);
  }

  /**
   * Update the gridData
   */
  useEffect(() => {
    getGridBoxData();
  }, [incidentState.pageEvents]);

  /**
   * Set the states and function to handle the sorting and filtering functionalities
   * within the common table component
   */
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterData, setFilterData] = useState({
    totalFilters: 0,
    filterTerms: {},
    searchTerm: "",
  });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortData, setSortData] = useState<ISortData>({
    field: "findingId",
    order: "desc",
  });

  async function onSortChange(field: string, order: string) {
    setSortData({ field, order });
    handleFilterData({
      filter: {},
      argSortOrder: order,
      argSortField: field === "id" ? "findingId" : field,
    });
  }

  useEffect(() => {
    handleFilterData({ filter: {} });
  }, []);

  async function onPageChange(pageNum) {
    handleFilterData({ filter: {}, argPageNum: pageNum });
  }

  async function handleRowsPerPageChange(pageSize) {
    setPageSize(pageSize);
    handleFilterData({ filter: {}, argPageSize: pageSize });
  }

  async function clearSearch() {
    const token = await checkAuthToken();

    setFilterData((prev) => ({
      filterTerms: filterData["filterTerms"],
      totalFilters: getFilterCount("", filterData["filterTerms"]),
      searchTerm: "",
    }));

    dispatch(
      setIncidentsByPage({
        target: "cases",
        aFAccessToken: token,
        page: page,
        size: pageSize,
        orderBy: sortData.field,
        order: sortData.order,
        filters: filterData["filterTerms"],
        search: "",
      })
    );
  }

  async function deleteFilter(key, value) {
    const token = await checkAuthToken();

    const tempTerms: { [key: string]: any } = filterData["filterTerms"];
    const filterValues = tempTerms[key];

    if (filterValues) {
      tempTerms[key] = filterValues
        .split(",")
        .filter((item) => item !== value)
        .join(",");

      for (const key in tempTerms) {
        if (tempTerms[key] === "") {
          delete tempTerms[key];
        }
      }

      setFilterData((prev) => ({
        filterTerms: tempTerms,
        totalFilters: getFilterCount(filterData["searchTerm"], tempTerms),
        searchTerm: filterData["searchTerm"],
      }));

      dispatch(
        setIncidentsByPage({
          target: "cases",
          aFAccessToken: token,
          page: page,
          size: pageSize,
          orderBy: sortData.field,
          order: sortData.order,
          filters: tempTerms ? tempTerms : filterData["filterTerms"],
          search: filterData["searchTerm"],
        })
      );
    }
  }

  async function clearFilters() {
    const token = await checkAuthToken();

    setFilterData((prev) => ({
      filterTerms: {},
      totalFilters: 0,
      searchTerm: "",
    }));

    dispatch(
      setIncidentsByPage({
        target: "cases",
        aFAccessToken: token,
        page: page,
        size: pageSize,
        orderBy: sortData.field,
        order: sortData.order,
        filters: {},
        search: "",
      })
    );
  }

  // Clear Filter button for the Mobile Desktop
  const headerState = useAppSelector((state) => state.header);
  useEffect(() => {
    clearFilters();
    clearSearch();
    dispatch(setFiltersApplied(false));
  }, [headerState.clearFilters]);

  async function handleSearchValue({ search }) {
    // Verify JWT from auth server
    const token = await checkAuthToken();

    // Build Params for the HTTP request
    const params: Params = {
      target: "cases",
      aFAccessToken: token,
      page: page,
      size: pageSize,
      orderBy: sortData.field,
      order: sortData.order,
      filters: filterData["filterTerms"],
      search: search,
    };

    // Send the HTTP request
    dispatch(setIncidentsByPage(params));

    setFilterData({
      totalFilters: getFilterCount(search, filterData["filterTerms"]),
      filterTerms: filterData["filterTerms"],
      searchTerm: search,
    });

    dispatch(setFiltersApplied(true));
  }

  async function handleFilterData({
    filter,
    argPageSize = pageSize,
    argPageNum = page,
    argSortField = sortData.field,
    argSortOrder = sortData.order,
  }) {
    const token = await checkAuthToken();

    const params: Params = {
      target: "cases",
      aFAccessToken: token,
      page: argPageNum,
      size: argPageSize,
      orderBy: argSortField,
      order: argSortOrder,
      filters: filterData["filterTerms"],
      search: filterData["searchTerm"],
    };

    // A new filter has been provided, parse an add replace the existing
    params.filters = getFilters(filter, params);

    // Request the filtered data from the backend
    dispatch(setIncidentsByPage(params));

    setFilterData({
      totalFilters: getFilterCount(filterData["searchTerm"], params.filters),
      filterTerms: params.filters,
      searchTerm: filterData["searchTerm"],
    });

    if (Object.keys(filter).length !== 0 || filterData["searchTerm"] !== "") {
      dispatch(setFiltersApplied(true));
    }
  }

  /**
   * Export the filtered data to Excel
   */
  async function handleExportButton() {
    dispatch(setExportLoading(true));
    const token = await checkAuthToken();

    const params: Params = {
      target: "cases",
      aFAccessToken: token,
      page: page,
      size: pageSize,
      orderBy: sortData.field,
      order: sortData.order,
      filters: filterData["filterTerms"],
      search: filterData["searchTerm"],
    };

    const pageEventsAction = await dispatch(fetchAllIncidentsByPage(params));

    // Using type assertion to help TypeScript understand the structure
    const result = pageEventsAction.payload as {
      exportEvents: {
        records: any[];
        totalCount: number;
      };
    };

    if (result && result.exportEvents && result.exportEvents.records) {
      await exportToCSV(result.exportEvents.records);
    } else {
      console.error(
        "Export data structure is invalid",
        pageEventsAction.payload
      );
    }

    dispatch(setExportLoading(false));
  }

  /**
   * Handle the expanded incident modal
   * @param id
   */
  const onIncidentClick = async (id, type) => {
    if (id) {
      dispatch(setSelectedRow({ id: id, type: type }));
      dispatch(setSideModal(true));
      // Navigate only if it's a specific action type
      if (type === "searchSimilar") {
        navigate(`/console/cr/flywheel/incidents/search`);
      }
    }
  };

  const onActionClick = async (rowId: string, type: string) => {
    console.log("onActionClick IncidentTable component ", rowId, type);
    dispatch(setSelectedRow({ id: rowId, type: type }));

    if (type === "searchAgentSuite") {
      console.log(`1`);
      showChatbot();

    } else if (type === "searchSimilar") {
      console.log(`2`);
      navigate(`/console/cr/flywheel/incidents/search`);

    } else if (type === "viewIncident") {
      console.log(`3`);
      dispatch(setSideModal(true));
    } else {
      console.log(`4`);
      console.log(`Unknown action type: ${type}`);
    }
    console.log("5");
  };

  useEffect(() => {
    // If not currently fetching and has already instanciated the first call then fetch on the interval
    const fetchData = async () => {
      const token = await checkAuthToken();

      // Get new data
      await dispatch(updatePage(token));
    };

    // Set the interval, every X seconds 'fetchData()' will be called
    const intervalId = setInterval(fetchData, 30000);

    // Once the component closes, close this interval: only fetch when in focus
    return () => {
      clearInterval(intervalId);
    };
  }, [page, pageSize, sortData, filterData]);

  return (
    <>
      <div
        className="flex h-full overflow-hidden"
        style={{ width: "100%", height: "100%" }}
      >
        <div
          className={`${incidentState.previewWidth === 100 ? "" : "ps-3 pb-3 pe-3"} flex-grow flex-shrink-1 overflow-scroll`}
          style={{
            flexGrow: 0,
            flexShrink: 1,
            width: `${
              incidentState.previewWidth
                ? 100 - incidentState.previewWidth
                : 100
            }%`,
            transition: "width 0.2s ease-in-out",
          }}
        >
          <GridPageLayout {...topGridBoxdata} />

          <IncidentsToolbar
            rowData={incidentState.pageEvents}
            headCells={incidentState.incidentHeaders as IHeadCell[]}
            setFilterData={handleFilterData}
            setSearchValue={handleSearchValue}
            isFilterModalOpen={isFilterModalOpen}
            setPage={setPage}
            setIsFilterModalOpen={setIsFilterModalOpen}
            handleExportButton={handleExportButton}
            filterData={filterData}
            deleteFilter={deleteFilter}
            clearSearch={clearSearch}
          />

          <DataTableGrid>
            <GridItem title={""}>
              <DarkDatagrid
                rowData={incidentState.pageEvents}
                headCells={incidentState.incidentHeaders as IHeadCell[]}
                visibleKeys={incidentState.incidentHeaders
                  .filter((header) => header.showColumnUnExpanded)
                  .map((header) => header.id)}
                maxWidths={{
                  entity: 200,
                  classification: 200,
                }}
                showSelectionCheckbox={false}
                showPagination={true}
                pageSize={pageSize}
                totalData={incidentState.totalRowCount}
                showCustomSort={true}
                isFilterModalOpen={isFilterModalOpen}
                setIsFilterModalOpen={setIsFilterModalOpen}
                defaultSorterColumn={sortData?.field}
                onRowClick={onIncidentClick}
                highlightedRowId={incidentState.selectedRow}
                handleAssignedTo={handleAssignedTo}
                users={users}
                setPage={setPage}
                page={page}
                isPageLoading={incidentState.areUserEventsLoading}
                statusMapping={statusMapping}
                onRowsPerPageChange={handleRowsPerPageChange}
                onPageChange={onPageChange}
                onSortChange={onSortChange}
                handleSetStatus={handleSetStatus}
                onActionClick={onActionClick}
              />
            </GridItem>
          </DataTableGrid>
        </div>

        {/* Resizable Side Panel for the Summary Section*/}
        <AlertSidePanel />
      </div>
    </>
  );
}
